import { getSnapshotAppDefaultInfo } from "helpers/globalContext/globalInfoSnapshot";

interface IAppDefaultFlagsType {
    [flag: string]: any;
}

/**
 * The raw, untyped app-default dictionary the webforms handler inlines on the page.
 *
 * `getAppDefaultFlagValue` below is called with keys not declared on `AppDefaultInfo`
 * (`enableSentry`, `enableMixpanel`, `mixpanelUrl`, `sawmillFrontendTenantId`), which would trip
 * the "flag not found" throw through the typed snapshot. Those callers are bootstrap-only and
 * owned by separate stories (282448 / 283956 / 282593), so the untyped path stays on the global.
 */
// eslint-disable-next-line @typescript-eslint/no-deprecated -- see above; bootstrap-only, owned by 282448 / 283956 / 282593
const btJScriptGlobals = window.btJScriptGlobals;
const flagsDictOrUndef: IAppDefaultFlagsType | undefined =
    btJScriptGlobals && btJScriptGlobals.appDefaultFlags;

const getFlagValue = (flag: string, defaultValue: any) => {
    const flagValue = flagsDictOrUndef ? flagsDictOrUndef[flag] : defaultValue;
    if (flagValue === undefined) {
        throw new Error(`App default flag ${flag} not found`);
    } else {
        return flagValue;
    }
};

/**
 * Given a string representing the key value of an App Default flag, returns the value stored with that flag if it exists
 * When App Default flag dictionary is undefined (e.g. when in Storybook), always returns true
 * @param flag key value of the App Default flag to check
 * @deprecated Use AppDefaultInfoContext instead
 * @example
 * <AppDefaultInfoContext.Consumer>
 *     {(appDefaultValues) => {
 *
 *     }}
 * </AppDefaultInfoContext.Consumer>
 */
export const getAppDefaultFlagValue = (flag: string, defaultValue: any = true): any => {
    const flagValue = getFlagValue(flag, defaultValue);
    return flagValue === undefined ? defaultValue : flagValue;
};

/**
 * Given a string representing the key value of an App Default flag, returns the value stored with that flag cast to a boolean if it exists
 * When App Default flag dictionary is undefined (e.g. when in Storybook), always returns true
 * @param flag key value of the App Default flag to check
 * @deprecated Use AppDefaultInfoContext instead
 * @example
 * <AppDefaultInfoContext.Consumer>
 *     {(appDefaultValues) => {
 *
 *     }}
 * </AppDefaultInfoContext.Consumer>
 */
export const getAppDefaultFlagValueAsBoolean = (flag: string): boolean => {
    const defaultValue = true;
    const flagValue = getFlagValue(flag, defaultValue);
    return flagValue === undefined ? defaultValue : !!flagValue;
};

/**
 * Read lazily rather than captured at module scope: the snapshot is only populated once
 * `AppProvider` resolves, and `redirectToLoginPage` can fire on a 401 before that.
 */
export const isBTAdminAuthProjectXEnabled = (): boolean =>
    Boolean(
        getSnapshotAppDefaultInfo()?.btAdminAuthProjectX ?? flagsDictOrUndef?.btAdminAuthProjectX,
    );
